import isEmpty from 'lodash/isEmpty';
import {
  IBuilder,
  ICohortRow,
  IErrors,
  IRow,
  RowType,
} from '../../../redux/slices/oldBuilder';
import { getCohortRowErrors } from '../../../utils/builder/cohorts';
import {
  CohortSampleOperationKey,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import {
  validateExpandEvents,
  validateMergeEvents,
} from './validateExecutionInput';

const getErrorsFromRowCohortFilters = (
  rows: IRow[],
  rowType: RowType.measures | RowType.dataTableMeasures,
): IErrors => {
  const errors: IErrors = {};

  const rowCohortFilters = rows.reduce<ICohortRow[]>((acc, row) => {
    if (row?.cohortFilter && row?.cohortFilter.isActive) {
      acc.push(row.cohortFilter);
    }

    return acc;
  }, []);

  if (!rowCohortFilters?.length) {
    return errors;
  }

  rowCohortFilters.forEach((cohortRow, index) => {
    const cohortRowErrors = getCohortRowErrors(cohortRow);

    if (!isEmpty(cohortRowErrors)) {
      errors[rowType] = {
        ...(errors?.[rowType] ?? {}),
        [index]: {
          ...(errors?.[rowType]?.[index] ?? {}),
          cohortFilter: cohortRowErrors,
        },
      };
    }
  });

  return errors;
};

export const getErrorsFromCohortRows = (
  cohortRows: ICohortRow[],
  rowType: RowType,
): IErrors => {
  const errors: IErrors = {};

  cohortRows.forEach((cohortRow, index) => {
    const cohortRowErrors = getCohortRowErrors(cohortRow);

    if (!isEmpty(cohortRowErrors)) {
      errors[rowType] = {
        ...(errors?.[rowType] ?? {}),
        [index]: cohortRowErrors,
      };
    }
  });

  return errors;
};

export const getCohortRowErrorsByQueryType = (
  state: IBuilder,
  queryType: QueryType,
): IErrors => {
  if (queryType === QueryType.CohortSample) {
    const errors = getErrorsFromCohortRows(
      state[RowType.builderCohorts],
      RowType.builderCohorts,
    );

    if (
      state.builderSampledCohort?.sampleOperation?.key ===
      CohortSampleOperationKey.Compare
    ) {
      validateExpandEvents(
        state[RowType.expandEvents],
        errors,
        RowType.expandEvents,
      );
      validateMergeEvents(
        state[RowType.mergeEvents],
        errors,
        RowType.mergeEvents,
      );
    }

    return errors;
  }

  if (queryType === QueryType.Prediction) {
    return getErrorsFromCohortRows(
      state[RowType.predictionCohorts],
      RowType.predictionCohorts,
    );
  }

  if (queryType === QueryType.Expression) {
    return {
      ...getErrorsFromRowCohortFilters(state.measures, RowType.measures),
      ...getErrorsFromCohortRows(
        state[RowType.globalCohorts],
        RowType.globalCohorts,
      ),
      ...getErrorsFromCohortRows(
        state[RowType.breakdownCohorts],
        RowType.breakdownCohorts,
      ),
    };
  }

  if (queryType === QueryType.DataTable) {
    return {
      ...getErrorsFromRowCohortFilters(
        state.dataTableMeasures,
        RowType.dataTableMeasures,
      ),
      ...getErrorsFromCohortRows(
        state[RowType.globalCohorts],
        RowType.globalCohorts,
      ),
      ...getErrorsFromCohortRows(
        state[RowType.breakdownCohorts],
        RowType.breakdownCohorts,
      ),
    };
  }

  return {
    ...getErrorsFromCohortRows(
      state[RowType.globalCohorts],
      RowType.globalCohorts,
    ),
    ...getErrorsFromCohortRows(
      state[RowType.breakdownCohorts],
      RowType.breakdownCohorts,
    ),
  };
};
